import { groupBy } from 'lodash-es';
import { buildDownloadData, getRegionByCode, getCountryByCode } from '@intelligence/utils/commonUtils';
import { formatDateStr, getDateRangeInTitle } from '@intelligence/v3/utils/date';
import {
  GSD_TOTAL_UNITS_METRIC_KEY,
  GSD_TOTAL_UNITS_METRIC_RATIO_KEY,
  GSD_DIGITAL_UNITS_METRIC_KEY,
  GSD_DIGITAL_UNITS_METRIC_RATIO_KEY,
  GSD_PHYSICAL_UNITS_METRIC_KEY,
  GSD_PHYSICAL_UNITS_METRIC_RATIO_KEY,
  GSD_TOTAL_REVENUE_METRIC_KEY,
  GSD_TOTAL_REVENUE_METRIC_RATIO_KEY,
  GSD_DIGITAL_REVENUE_METRIC_KEY,
  GSD_DIGITAL_REVENUE_METRIC_RATIO_KEY,
  GSD_PHYSICAL_REVENUE_METRIC_KEY,
  GSD_PHYSICAL_REVENUE_METRIC_RATIO_KEY,
} from '@intelligence/v3/_config/metrics/gsd';
import {
  MSCIENCE_DIGITAL_REVENUE_METRIC_KEY,
  MSCIENCE_DIGITAL_REVENUE_METRIC_RATIO_KEY,
  MSCIENCE_DIGITAL_UNITS_METRIC_KEY,
  MSCIENCE_DIGITAL_UNITS_METRIC_RATIO_KEY,
} from '@intelligence/v3/_config/metrics/mscience';
import {
  NPD_PHYSICAL_REVENUE_METRIC_KEY,
  NPD_PHYSICAL_REVENUE_METRIC_RATIO_KEY,
  NPD_PHYSICAL_UNITS_METRIC_KEY,
  NPD_PHYSICAL_UNITS_METRIC_RATIO_KEY,
} from '@intelligence/v3/_config/metrics/npd';
import {
  NEWZOO_TOTAL_REVENUE_METRIC_KEY,
  NEWZOO_TOTAL_PLAYERS_METRIC_KEY,
} from '@intelligence/v3/_config/metrics/newzoo';
import { DataCalculation } from '@intelligence/v3/enums/download';
import { DOWNLOAD_CFG_KEY } from '@intelligence/v3/constants';

const formatList = <T extends IGenreMarketProfile>(list?: T[] | null, allParams?: any): T[] => {
  if (!Array.isArray(list) || list.length === 0) return [];
  const markets = allParams?.markets || [];
  const isGroupByMarket = allParams?.[DOWNLOAD_CFG_KEY.REGION_METHOD] === DataCalculation.SeparateByCountry;
  if (!isGroupByMarket || !Array.isArray(markets) || markets.length === 0) return [...list];
  const listGroup = groupBy(list, 'market');
  return markets.reduce((acc, cur) => {
    const curList = listGroup?.[cur] || [];
    return [...acc, ...curList];
  }, []);
};

export const getMarketProfileTopChartsCombineConfig = (
  data: IGenreMarketProfilePaginationDataNewzoo,
  params: any,
  allParams: any,
) => {
  // @ts-ignore
  const req = data?.$req || {};
  const granularity = params?.granularity || 'daily';
  const regionOptions = allParams?.regionOptions || [];
  const isGroupByDate = allParams?.[DOWNLOAD_CFG_KEY.DATE_METHOD] !== DataCalculation.Aggregated;
  const isGroupByRegion = allParams?.[DOWNLOAD_CFG_KEY.REGION_METHOD] === DataCalculation.SeparateByRegion;
  const isGroupByMarket = allParams?.[DOWNLOAD_CFG_KEY.REGION_METHOD] === DataCalculation.SeparateByCountry;
  const isGoupByGenre = allParams?.[DOWNLOAD_CFG_KEY.GENRE_METHOD] === DataCalculation.SeparateByGenre;
  const isGoupBySubGenre = allParams?.[DOWNLOAD_CFG_KEY.GENRE_METHOD] === DataCalculation.SeparateBySubGenre;
  // 是否展示%
  const isShowRatio = !!allParams?.ratio;
  const dateRangeInTitle = getDateRangeInTitle('yearly', req?.newzoo_start_date, req?.newzoo_end_date);
  // 需要下载的指标映射
  const metricMap: IRecord<boolean> = params?.metrics?.reduce((acc: IRecord<boolean>, cur: string) => {
    return { ...acc, [cur]: true };
  }, {});

  const ext = data?.$ext || {};
  const marketInfo = ext?.market_info || {};
  const regionNameENMap = marketInfo?.['intel_region -> en'] || {};
  const regionNameCNMap = marketInfo?.['intel_region -> cn'] || {};
  const marketNameENMap = marketInfo?.['market -> en'] || {};
  const marketNameCNMap = marketInfo?.['market -> cn'] || {};
  const regionCodeMap = marketInfo?.['market -> region'] || {};

  const regionCountryConfig = [
    {
      title: 'Region_English',
      // key: (item: any) => getRegionByCode(item?.region, regionOptions)?.area_name_en || '',
      key: (item: any) => {
        if (isGroupByRegion) {
          const itemRegionCode = item?.region || '';
          return regionNameENMap?.[itemRegionCode] || itemRegionCode;
        }

        const itemMarketCode = item?.market || '';
        const itemRegionCode = regionCodeMap?.[itemMarketCode]?.toLowerCase() || '';
        return regionNameENMap?.[itemRegionCode] || itemRegionCode;
      },
      when: () => isGroupByRegion,
    },
    {
      title: 'Region_Chinese',
      // key: (item: any) => getRegionByCode(item?.region, regionOptions)?.area_name_ch || '',
      key: (item: any) => {
        if (isGroupByRegion) {
          const itemRegionCode = item?.region || '';
          return regionNameCNMap?.[itemRegionCode] || itemRegionCode;
        }

        const itemMarketCode = item?.market || '';
        const itemRegionCode = regionCodeMap?.[itemMarketCode]?.toLowerCase() || '';
        return regionNameCNMap?.[itemRegionCode] || itemRegionCode;
      },
      when: () => isGroupByRegion,
    },
    {
      title: 'Country_English',
      // key: (item: any) => getCountryByCode(item?.market, regionOptions)?.country_name_en || '',
      key: (item: any) => {
        const itemMarketCode = item?.market || '';
        return marketNameENMap?.[itemMarketCode] || itemMarketCode;
      },
      when: () => isGroupByMarket,
    },
    {
      title: 'Country_Chinese',
      // key: (item: any) => getCountryByCode(item?.market, regionOptions)?.country_name_ch || '',
      key: (item: any) => {
        const itemMarketCode = item?.market || '';
        return marketNameCNMap?.[itemMarketCode] || itemMarketCode;
      },
      when: () => isGroupByMarket,
    },
  ];

  // const regionCountryConfig = [
  //   {
  //     title: 'Region_English',
  //     key: (item: any) => getRegionByCode(item?.region, regionOptions)?.area_name_en || '',
  //     when: () => isGroupByRegion,
  //   },
  //   {
  //     title: 'Region_Chinese',
  //     key: (item: any) => getRegionByCode(item?.region, regionOptions)?.area_name_ch || '',
  //     when: () => isGroupByRegion,
  //   },
  //   {
  //     title: 'Country_English',
  //     key: (item: any) => getCountryByCode(item?.market, regionOptions)?.country_name_en || '',
  //     when: () => isGroupByMarket,
  //   },
  //   {
  //     title: 'Country_Chinese',
  //     key: (item: any) => getCountryByCode(item?.market, regionOptions)?.country_name_ch || '',
  //     when: () => isGroupByMarket,
  //   },
  // ];

  const genreConfig = [
    {
      title: 'Genre',
      key: (item: any) => item?.genre || '',
      when: () => isGoupByGenre,
    },
    {
      title: 'Sub_Genre',
      key: (item: any) => item?.sub_genre || '',
      when: () => isGoupBySubGenre,
    },
  ];

  const commonSheetConfig = [
    {
      title: 'Date',
      key: (item: any) => {
        const itemDate = item?.date || '';
        return granularity === 'monthly' ? formatDateStr(itemDate, 'YYYY-MM') : itemDate;
      },
      when: () => isGroupByDate,
    },
    ...regionCountryConfig,
    ...genreConfig,
    {
      title: 'Total Units',
      key: (item: any) => item?.[GSD_TOTAL_UNITS_METRIC_KEY] || '',
      when: () => metricMap?.[GSD_TOTAL_UNITS_METRIC_KEY],
    },
    {
      title: '%',
      key: (item: any) => item?.[GSD_TOTAL_UNITS_METRIC_RATIO_KEY] || '',
      when: () => isShowRatio && metricMap?.[GSD_TOTAL_UNITS_METRIC_KEY],
    },
    {
      title: 'Digital Units',
      key: (item: any) => item?.[GSD_DIGITAL_UNITS_METRIC_KEY] || '',
      when: () => metricMap?.[GSD_DIGITAL_UNITS_METRIC_KEY],
    },
    {
      title: '%',
      key: (item: any) => item?.[GSD_DIGITAL_UNITS_METRIC_RATIO_KEY] || '',
      when: () => isShowRatio && metricMap?.[GSD_DIGITAL_UNITS_METRIC_KEY],
    },
    {
      title: 'Physical Units',
      key: (item: any) => item?.[GSD_PHYSICAL_UNITS_METRIC_KEY] || '',
      when: () => metricMap?.[GSD_PHYSICAL_UNITS_METRIC_KEY],
    },
    {
      title: '%',
      key: (item: any) => item?.[GSD_PHYSICAL_UNITS_METRIC_RATIO_KEY] || '',
      when: () => isShowRatio && metricMap?.[GSD_PHYSICAL_UNITS_METRIC_KEY],
    },
    {
      title: 'Total Revenue',
      key: (item: any) => item?.[GSD_TOTAL_REVENUE_METRIC_KEY] || '',
      when: () => metricMap?.[GSD_TOTAL_REVENUE_METRIC_KEY],
    },
    {
      title: '%',
      key: (item: any) => item?.[GSD_TOTAL_REVENUE_METRIC_RATIO_KEY] || '',
      when: () => isShowRatio && metricMap?.[GSD_TOTAL_REVENUE_METRIC_KEY],
    },
    {
      title: 'Digital Revenue',
      key: (item: any) => item?.[GSD_DIGITAL_REVENUE_METRIC_KEY] || '',
      when: () => metricMap?.[GSD_DIGITAL_REVENUE_METRIC_KEY],
    },
    {
      title: '%',
      key: (item: any) => item?.[GSD_DIGITAL_REVENUE_METRIC_RATIO_KEY] || '',
      when: () => isShowRatio && metricMap?.[GSD_DIGITAL_REVENUE_METRIC_KEY],
    },
    {
      title: 'Physical Revenue',
      key: (item: any) => item?.[GSD_PHYSICAL_REVENUE_METRIC_KEY] || '',
      when: () => metricMap?.[GSD_PHYSICAL_REVENUE_METRIC_KEY],
    },
    {
      title: '%',
      key: (item: any) => item?.[GSD_PHYSICAL_REVENUE_METRIC_RATIO_KEY] || '',
      when: () => isShowRatio && metricMap?.[GSD_PHYSICAL_REVENUE_METRIC_KEY],
    },

    {
      title: 'Digital Revenue',
      key: (item: any) => item?.[MSCIENCE_DIGITAL_REVENUE_METRIC_KEY] || '',
      when: () => metricMap?.[MSCIENCE_DIGITAL_REVENUE_METRIC_KEY],
    },
    {
      title: '%',
      key: (item: any) => item?.[MSCIENCE_DIGITAL_REVENUE_METRIC_RATIO_KEY] || '',
      when: () => isShowRatio && metricMap?.[MSCIENCE_DIGITAL_REVENUE_METRIC_KEY],
    },
    {
      title: 'Digital Units',
      key: (item: any) => item?.[MSCIENCE_DIGITAL_UNITS_METRIC_KEY] || '',
      when: () => metricMap?.[MSCIENCE_DIGITAL_UNITS_METRIC_KEY],
    },
    {
      title: '%',
      key: (item: any) => item?.[MSCIENCE_DIGITAL_UNITS_METRIC_RATIO_KEY] || '',
      when: () => isShowRatio && metricMap?.[MSCIENCE_DIGITAL_UNITS_METRIC_KEY],
    },
    {
      title: 'Physical Revenue',
      key: (item: any) => item?.[NPD_PHYSICAL_REVENUE_METRIC_KEY] || '',
      when: () => metricMap?.[NPD_PHYSICAL_REVENUE_METRIC_KEY],
    },
    {
      title: '%',
      key: (item: any) => item?.[NPD_PHYSICAL_REVENUE_METRIC_RATIO_KEY] || '',
      when: () => isShowRatio && metricMap?.[NPD_PHYSICAL_REVENUE_METRIC_KEY],
    },
    {
      title: 'Physical Units',
      key: (item: any) => item?.[NPD_PHYSICAL_UNITS_METRIC_KEY] || '',
      when: () => metricMap?.[NPD_PHYSICAL_UNITS_METRIC_KEY],
    },
    {
      title: '%',
      key: (item: any) => item?.[NPD_PHYSICAL_UNITS_METRIC_RATIO_KEY] || '',
      when: () => isShowRatio && metricMap?.[NPD_PHYSICAL_UNITS_METRIC_KEY],
    },
  ];

  const yearlySheetConfig = [
    ...regionCountryConfig,
    ...genreConfig,
    {
      title: `Total Revenue(${dateRangeInTitle})`,
      key: (item: any) => item?.[NEWZOO_TOTAL_REVENUE_METRIC_KEY] || '',
      when: () => !!metricMap?.[NEWZOO_TOTAL_REVENUE_METRIC_KEY],
    },
    {
      title: `Total Players(${dateRangeInTitle})`,
      key: (item: any) => item?.[NEWZOO_TOTAL_PLAYERS_METRIC_KEY] || '',
      when: () => !!metricMap?.[NEWZOO_TOTAL_PLAYERS_METRIC_KEY],
    },
  ];

  return {
    stat: {
      sheetName: 'Digital Revenue&Physical Revenue',
      style: 'normal',
      data: buildDownloadData(commonSheetConfig, formatList(data?.list, allParams)),
    },
    summary: {
      sheetName: `DataBrain ${dateRangeInTitle}`,
      style: 'normal',
      data: buildDownloadData(yearlySheetConfig, formatList(data?.newzoo_list, allParams)),
    },
  };
};
